import { useAtom } from "jotai";
import { useState } from "react";
import { useBeforeUnload } from "lib/hooks/useBeforeUnload";
import { useOnce } from "lib/hooks/useOnce";
import {
  isJabraConnectedAtom,
  mediaRecorderAtom,
  recordedVisitIdAtom,
  recordingStatusAtom,
} from "lib/hooks/useVisitRecorder/atoms";
import { useVisitData } from "views/Visit/hooks";
import {
  checkJabraConnection,
  createAudioBlobFromBlobs,
  createAudioURLFromBlobs,
  indexedDBProxy,
  recordingOfficeIds,
  uploadRecording,
} from "./tools";

export const useVisitRecorder = () => {
  const { data: visit } = useVisitData({ from: "cache-only" });
  const [recordingStatus, setRecordingStatus] = useAtom(recordingStatusAtom);
  const [mediaRecorder, setMediaRecorder] = useAtom(mediaRecorderAtom);
  const [isJabraConnected, setIsJabraConnected] = useAtom(isJabraConnectedAtom);
  const [recordedVisitId, setRecordedVisitId] = useAtom(recordedVisitIdAtom);
  const [, setLLMAudioURI] = useState<string | null>(null);
  const [, setVisitAudioURI] = useState<string | null>(null);

  useOnce(
    () => {
      if (!visit) return;

      indexedDBProxy.get(visit.id).then((blobs) => {
        if (recordingStatus === "idle" && blobs.length > 0) {
          startRecording(); // resume after an interruption
        }
      });
    },
    { when: Boolean(visit) },
  );

  useBeforeUnload((event) => {
    if (recordingStatus === "recording") {
      event.preventDefault();
    }
  });

  const isPersonalVisit = visit && visit.slotType === "PERSONAL";

  const hasRecordingConsentBeenGiven =
    visit && visit.patient?.visitRecordingAndAnalysisAgreement;

  const isRecordingOffice =
    visit &&
    visit.organizationId &&
    recordingOfficeIds.includes(visit.organizationId);

  const shouldRecord =
    isPersonalVisit && hasRecordingConsentBeenGiven && isRecordingOffice;

  const startRecording = async () => {
    if (!shouldRecord || mediaRecorder) return;

    if (!visit) return;

    try {
      setIsJabraConnected(await checkJabraConnection());

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const media = new MediaRecorder(stream);

      navigator.mediaDevices.addEventListener("devicechange", async () => {
        setIsJabraConnected(await checkJabraConnection());
      });

      setMediaRecorder(media);

      media.ondataavailable = (event) => {
        indexedDBProxy.put(visit.id, event.data);
      };

      media.onpause = async () => {
        const audioURL = await createAudioURLFromBlobs(visit.id);

        setLLMAudioURI(audioURL);
      };

      media.onstop = async () => {
        const audioURL = await createAudioURLFromBlobs(visit.id);
        const audioBlob = await createAudioBlobFromBlobs(visit.id);

        setVisitAudioURI(audioURL);

        try {
          await uploadRecording(audioBlob, visit.id);
          setRecordingStatus("success");
          setRecordedVisitId(null);
        } catch (error) {
          console.error("Error uploading recording:", error);
          setRecordingStatus("error");
          setRecordedVisitId(null);
        }

        stream.getTracks().forEach((track) => track.stop());

        setMediaRecorder(null);

        setTimeout(() => {
          indexedDBProxy.del(visit.id);
        }, 100);
      };

      media.start(500);

      setRecordingStatus("recording");
      setRecordedVisitId(visit.id);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setRecordingStatus("error");
      setRecordedVisitId(null);
    }
  };

  const stopRecording = () => {
    if (!shouldRecord || !mediaRecorder) return;

    setTimeout(() => {
      mediaRecorder.stop();
    }, 100);
  };

  const saveRecordingForLLM = () => {
    if (!shouldRecord || !mediaRecorder) return;

    setTimeout(() => {
      mediaRecorder.pause();
      mediaRecorder.resume();
    }, 100);
  };

  return {
    recordingStatus,
    recordedVisitId,
    isJabraConnected,
    startRecording,
    stopRecording,
    saveRecordingForLLM,
  };
};
